import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import type { SearchResults as Results, SearchResultEntryWithType, TabTypes } from '../../../../types/state';
import { AppStateType } from '../../../constants/enums';
import { locales } from '../../../locales/en';
import { openSettingsDrawer } from '../../../state/modals/state.drawer';
import { search, updateSearchQuery } from '../../../state/state.search';
import { safeObj } from '../../../utilities/safe';
import Button from '../../Buttons/Button';
import { useAppStateSelector } from '../../Hooks/useAppState';
import { useSearch } from '../../Hooks/useSearch';
import NoResults from '../Search/NoResults';
import SearchResults from '../Search/SearchResults';
import "./Search.linaria.module.css!=!./Search?./Search.linaria.module.css?css=H4sIAAAAAAAAA62RTZKDIBCF97nHLEnpxKQSPIyFgIoCTbCNGit3n/KvxmSS3aygv35NvX7s69B2d3+oBqFqp1lPSSvTSiFJoYtfWablBpp6AlvhJCikygukYRB8xa0SWMxXuEmfaWhpoYSQNn7s9gxNciyTsI8i0NmQghfSE8+EamoauW7U1GFZ4rm0w4dnN36JY7yiXFqUPl7tPcNFXDY1qqwnHCxKi2v7A/6/aMZ9zDU8XvrBsI7Ma5yCwHWxY0IomxMER8+bOgVEME9IywyfgJ+SGcnWDhHKS44KLOWgG2N/M/nTeU8N87mylDUIk3XHzpJfhxnPNqbeAmYbE8nAImnnHzsFQYyyQ8K0yu0a6vL9zCdh6iKo+Es5vLe0jKUyCa8XzvP7SzmPtZ45amE81onbPQllcPjuiqFVWhNeMJtLip7ZOgNv4sfuB3ZFMH4NAwAA"
const StyledGridContainer = /*#__PURE__*/styled('div')({
  name: "StyledGridContainer",
  class: "s1nxzr3k",
  propsAsIs: false
});
const darkModeRoundedCorners = "atm_5j_1y44olf";
const StyledRoot = /*#__PURE__*/styled('div')({
  name: "StyledRoot",
  class: "s1jjt8jn",
  propsAsIs: false
});
const StyledContainer = /*#__PURE__*/styled('div')({
  name: "StyledContainer",
  class: "smq159y",
  propsAsIs: false
});
const StyledMessageContainer = /*#__PURE__*/styled('div')({
  name: "StyledMessageContainer",
  class: "spa8ecq",
  propsAsIs: false
});
const portraitStyles = "atm_ar_1bp4okc atm_be_1q9ccgz atm_vz_1e032xh";
const EmptyListWarning = memo(() => {
  const onOpenSetttings = useCallback(() => {
    openSettingsDrawer();
  }, []);
  return <StyledRoot data-testid="Home__EmptyListWarning">
            <StyledContainer>
                <StyledMessageContainer>
                    {locales.home.noModels}
                </StyledMessageContainer>
                <br />
                <Button size="large" onClick={onOpenSetttings}>
                    {locales.home.openSettings}
                </Button>
            </StyledContainer>
        </StyledRoot>;
});
const searchTypes: TabTypes[] = ['name', 'topic', 'tags'];
function getSearchResultModels(results: Results) {
  const {
    data: searchResultData
  } = results;
  // a model can match more than one list (e.g. her name in the topic) -
  // merge to one entry per model and track every list she matched
  const models = new Map<number, SearchResultEntryWithType>();
  searchTypes.forEach(type => {
    const {
      data: searchData
    } = safeObj(searchResultData?.[type]);
    if (searchData) {
      searchData.forEach(model => {
        const existing = models.get(model.id);
        if (existing) {
          if (!existing.types.includes(type)) {
            existing.types.push(type);
          }
        } else {
          models.set(model.id, {
            ...model,
            type,
            types: [type]
          });
        }
      });
    }
  });
  const modelsArray = Array.from(models.values());
  modelsArray.sort((a, b) => {
    return a.isOnline === b.isOnline ? 0 : a.isOnline ? -1 : 1;
  });
  return modelsArray;
}
interface SearchProps {
  noModelsVisible: boolean;
  children?: ReactNode;
}
const Search = memo((props: SearchProps) => {
  const {
    noModelsVisible,
    children
  } = props;
  const {
    results: searchResults,
    query,
    loading: searchLoading
  } = useSearch();
  const {
    type: searchType,
    fromHomePage
  } = query;
  const searchString = searchResults.query?.search || '';
  const searchResultModels = useMemo(() => getSearchResultModels(searchResults), [searchResults]);
  const hasSearchResults = fromHomePage && searchResultModels.length > 0 && searchString.length > 0;
  const searchingWithNoResults = fromHomePage && !searchLoading && searchResultModels.length === 0 && searchString.length > 0;
  const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode');
  const gridContainerStyles = cx(portraitStyles,
  // [!vertical ? portraitStyles : landscapeStyles]
  darkMode && darkModeRoundedCorners);
  const onTagClick = useCallback((tag: string) => {
    updateSearchQuery({
      search: tag,
      external: true,
      fromHomePage: true
    });
    search();
  }, []);
  return <StyledGridContainer className={gridContainerStyles}>
            {/*
                pins should be here when:
                - phone portrait
                - phone landscape
                - tablet portrait
                - tablet landscape
                - desktop portrait
             */}
            {/* {leftPin && PinnedRoomsContainer} */}
            {searchingWithNoResults ? <NoResults searchQuery={searchString} /> : hasSearchResults ? <SearchResults type={searchType} searchResults={searchResults} resultModels={searchResultModels} onTagClick={onTagClick} /> : !noModelsVisible ? <>{children}</> : <EmptyListWarning />}
            {/*
                pins should be here when:
                - desktop landscape
             */}
            {/* {!leftPin && PinnedRoomsContainer} */}
        </StyledGridContainer>;
});
export default Search;